/*
 * Reminders dashboard — /reminders
 *
 * Top filters: assignee (defaults to logged-in team member), status, priority,
 * due date range. Bottom: table of tasks. "+ New reminder" opens the form in a
 * CustomModal.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
  FaRegBell,
  FaPlus,
  FaCheckSquare,
  FaRegSquare,
  FaClock,
  FaUserCircle,
  FaLink,
  FaFilter,
  FaRedoAlt,
  FaTag,
  FaBuilding,
  FaRegCalendarAlt,
  FaTimes,
  FaRegEdit,
  FaRegTrashAlt,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { FaEllipsisVertical, FaRegNoteSticky, FaCircleInfo } from 'react-icons/fa6';
import { TbCircleLetterT, TbCircleLetterS } from 'react-icons/tb';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import ConnectionState from '../../../myt_chat/chat/enums/ConnectionState';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import { ChatClient } from '../../../myt_chat/worker/chatProxies';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import Paginator from '../../util_components/paginator';
import ConfirmationPopup from '../../util_components/confirmation_popup';
import { getCookie } from '../../../util_functions';
import TaskTooltipContent from './TaskTooltipContent';
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  INVOLVEMENT_OPTIONS,
  PERSISTED_FILTER_KEYS,
  normalizeDepartmentFilter,
  activeFilterSummary,
  formatRelativeDue,
  isPastDue,
  isDueSoon,
  isDueWithinHour,
  taskTooltipStyle,
  readableTextOn
} from './constants';
import {
  list_tasks,
  list_team_members,
  list_labels,
  list_departments,
  create_task,
  update_task,
  delete_task,
  team_member_by_uuid,
  linked_user_by_uuid,
  label_by_uuid,
  department_by_uuid,
  current_team_member,
  UNASSIGNED_ASSIGNEE,
  NO_DEPARTMENT
} from './api';
import ReminderForm from './ReminderForm';
import ReminderNotes from './ReminderNotes';
import PriorityIcon from './PriorityIcon';
import SearchableSelect from './SearchableSelect';
import ReminderDetailsModal from './ReminderDetailsModal';
import RemindersPresets from './RemindersPresets';
import { readPresets, getDefaultPreset } from './presets';
import './reminders.css';

function FilterLabel({ children }) {
  return (
    <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-content-muted">
      {children}
    </div>
  );
}

// Convert a task row into the shape ReminderForm expects in its `initial` prop.
// (Same shape used by RemindersModal — duplicated here to avoid a circular dep.)
const taskToFormInitial = (task) => {
  if (!task) return undefined;
  const m = moment(task.due_at);
  return {
    uuid: task.uuid,
    description: task.description || '',
    due_date: m.isValid() ? m.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    due_time: m.isValid() ? m.format('HH:mm') : moment().add(1, 'hour').format('HH:mm'),
    has_time: !!task.has_time,
    priority: task.priority || 'Medium',
    assignee_uuids: Array.isArray(task.assignee_uuids) ? task.assignee_uuids : [],
    linked_user_uuid: task.linked_user_uuid || '',
    label_uuids: task.label_uuids || [],
    alert_one: task.alerts?.[0]?.offset_minutes ?? '',
    alert_two: task.alerts?.[1]?.offset_minutes ?? '',
    attachments: Array.isArray(task.attachments) ? task.attachments : [],
    department_uuid: task.department_uuid || ''
  };
};

// Custom sidebar ranges for the react-date-range picker. Overrides the
// library defaults (which omit "Tomorrow" / "Next Week" / "Next Month").
// Built lazily so "today" is computed at the moment the picker opens.
const buildStaticRanges = () =>
  createStaticRanges([
    {
      label: 'Today',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Tomorrow',
      range: () => ({
        startDate: moment().add(1, 'day').startOf('day').toDate(),
        endDate: moment().add(1, 'day').endOf('day').toDate()
      })
    },
    {
      label: 'Yesterday',
      range: () => ({
        startDate: moment().subtract(1, 'day').startOf('day').toDate(),
        endDate: moment().subtract(1, 'day').endOf('day').toDate()
      })
    },
    {
      label: 'This Week',
      range: () => ({
        startDate: moment().startOf('week').toDate(),
        endDate: moment().endOf('week').toDate()
      })
    },
    {
      label: 'Next Week',
      range: () => ({
        startDate: moment().add(1, 'week').startOf('week').toDate(),
        endDate: moment().add(1, 'week').endOf('week').toDate()
      })
    },
    {
      label: 'Last Week',
      range: () => ({
        startDate: moment().subtract(1, 'week').startOf('week').toDate(),
        endDate: moment().subtract(1, 'week').endOf('week').toDate()
      })
    },
    {
      label: 'This Month',
      range: () => ({
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate()
      })
    },
    {
      label: 'Next Month',
      range: () => ({
        startDate: moment().add(1, 'month').startOf('month').toDate(),
        endDate: moment().add(1, 'month').endOf('month').toDate()
      })
    },
    {
      label: 'Last Month',
      range: () => ({
        startDate: moment().subtract(1, 'month').startOf('month').toDate(),
        endDate: moment().subtract(1, 'month').endOf('month').toDate()
      })
    }
  ]);

/**
 * Trigger button + popover wrapping react-date-range's DateRange calendar.
 * Returns YYYY-MM-DD strings via onChange({ from, to }).
 */
function DateRangeFilter({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Position the popover relative to the trigger and recompute whenever the
  // page scrolls / resizes so it stays anchored. Rendered via a portal so the
  // overflow-auto on .common-page-container can't clip it.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const isEmpty = !from && !to;
  // The DateRange component manages a stateful range object. When the user
  // hasn't picked anything yet we seed it with today's date so the calendar
  // opens to "now" rather than the epoch.
  const range = [
    {
      startDate: from ? new Date(from) : new Date(),
      endDate: to ? new Date(to) : new Date(),
      key: 'selection'
    }
  ];

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;
    // Emit timezone-aware RFC3339 bounds covering the picked days in the
    // BROWSER's zone. A bare YYYY-MM-DD would be read as UTC midnight by the
    // server, which for a non-UTC operator shifts the whole window (an IST
    // "today" would start at 05:30 IST and run 5.5h into the next day).
    onChange({
      from: moment(startDate).startOf('day').format(),
      to: moment(endDate).endOf('day').format()
    });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ from: '', to: '' });
  };

  const label = isEmpty
    ? 'Any date'
    : `${moment(from).format('MMM D')} → ${moment(to).format('MMM D')}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="reminders-trigger-btn inline-flex items-center gap-2 text-sm"
      >
        <FaRegCalendarAlt className="text-content-muted" size={12} />
        <span className={isEmpty ? 'text-content-muted' : ''}>{label}</span>
        {!isEmpty ? (
          <FaTimes
            size={10}
            onClick={handleClear}
            className="text-content-muted hover:text-content-secondary"
          />
        ) : null}
      </button>
      {open
        ? createPortal(
            // Rendered into <body>, so .common-page-container's overflow-auto
            // can't clip it. `.reminders-scope` is duplicated here so the
            // scoped overrides in reminders.css still apply to the
            // react-date-range internals.
            <div
              ref={popoverRef}
              className="reminders-scope"
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 10000
              }}
            >
              <div className="overflow-hidden rounded-md border border-line bg-surface-card shadow-lg">
                {/* DateRangePicker = the full layout: predefined-ranges sidebar
                    (Today, Yesterday, This Week, Last Week, This Month, Last Month)
                    + the input-range fields + the two-calendar selector. */}
                <DateRangePicker
                  ranges={range}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  rangeColors={['#ee731b']}
                  showDateDisplay={false}
                  months={2}
                  direction="horizontal"
                  /* Hide the "N days up to today / starting today" input fields
               — the static-range sidebar already covers the common cases. */
                  inputRanges={[]}
                  staticRanges={buildStaticRanges()}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/**
 * Kebab-style row actions menu. The table card has `overflow:hidden`, so a
 * pure `position:absolute` popover would clip on the last few rows. We
 * portal the menu to the body and anchor it to the trigger's right edge.
 */
function RowActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      // Anchor menu to the trigger's right edge, just below it.
      setCoords({ top: r.bottom + 4, left: r.right - 180 });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inTrig = triggerRef.current && triggerRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inTrig && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-tooltip-id="my-tooltip"
        data-tooltip-content="More actions"
        aria-label="More actions"
        className="reminders-icon-btn"
      >
        <FaEllipsisVertical size={14} />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="reminders-scope"
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 10000,
                width: 180
              }}
            >
              <div className="overflow-hidden rounded-md border border-line bg-surface-card shadow-lg">
                {items.map((it) => (
                  <button
                    key={it.label}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      it.onClick();
                    }}
                    className="reminders-neutral-btn flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    style={it.destructive ? { color: 'var(--state-error)' } : undefined}
                  >
                    {it.icon ? <it.icon size={13} /> : null}
                    <span className="flex-1">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

// Persisted filter state lives under this versioned key. Bump the version
// suffix if the persisted shape ever changes incompatibly, so an old payload is
// ignored rather than mis-hydrated.
const FILTERS_STORAGE_KEY = 'reminders_filters_v1';
// PERSISTED_FILTER_KEYS (the persisted filter subset — due-date range excluded)
// is imported from ./constants so index.jsx and presets.js share one source.

// Merge a stored filter subset (from "last used" persistence or a saved preset)
// over a base filters object, in place. Only the known keys are copied, each
// type-validated, so an old payload missing new keys — or carrying unknown ones
// — degrades gracefully to the base defaults.
function mergeFilterSubset(base, src) {
  if (!src || typeof src !== 'object') return;
  const involvementValues = INVOLVEMENT_OPTIONS.map((o) => o.value);
  if (Array.isArray(src.assignee_uuids)) {
    base.assignee_uuids = src.assignee_uuids.filter((v) => typeof v === 'string');
  }
  if (Array.isArray(src.involvement)) {
    base.involvement = src.involvement.filter((v) => involvementValues.includes(v));
  }
  // Departments went from single-select string to multi-select array; only
  // overwrite when the source actually carries one of the two shapes, so a
  // payload predating the field doesn't clobber the base value.
  if (Array.isArray(src.department_uuids) || typeof src.department_uuid === 'string') {
    base.department_uuids = normalizeDepartmentFilter(src);
  }
  ['status', 'priority', 'label_uuid'].forEach((k) => {
    if (typeof src[k] === 'string') base[k] = src[k];
  });
}

// Read the persisted filter payload, tolerating a missing/corrupt/old value —
// any problem yields null so the caller falls back to defaults.
function readPersistedFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function Reminders() {
  const history = useHistory();
  const location = useLocation();
  const cms_email = getCookie('cms_email');
  // CMS-wide timezone from the nav bar dropdown. Drives the rich tooltip and
  // any other time rendering on this page.
  const ianaTimezone = useSelector((state) => state.home?.iana_timezone || moment.tz.guess());

  const [team, setTeam] = useState([]);
  // Resolve the logged-in team member. Prefer a direct match in the loaded
  // `team` list (so `me` recomputes once list_team_members() resolves, even on
  // a cold cache); fall back to the shared cache lookup otherwise.
  const me = useMemo(
    () => team.find((m) => m.email === cms_email) || current_team_member(cms_email),
    [cms_email, team]
  );
  const [labels, setLabels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Server-reported match count for the current filters, which can exceed the
  // rows returned when the result set is larger than the backend's page limit.
  const [total, setTotal] = useState(0);
  // Lookup map for the description-tooltip render callback (which only
  // receives the anchor's `content` string — we pass the task uuid through
  // and resolve to the full task object here).
  const tasksByUuid = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      m[t.uuid] = t;
    });
    return m;
  }, [tasks]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // editingTask carries a uuid so submit becomes an update; null means the
  // form modal is in create mode.
  const [editingTask, setEditingTask] = useState(null);
  // Read-only "view details + notes" modal — separate from create/edit.
  const [viewingTask, setViewingTask] = useState(null);
  // Task pending delete-confirmation (null when no confirm dialog is open).
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  // Default sort: by due date ascending — most pressing reminders at the top.
  const [sortKey, setSortKey] = useState('due_at');
  const [sortDir, setSortDir] = useState('asc');
  // Client-side pagination over the (filtered + sorted) list.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // Default due-date window: today through the next 30 days — covers what's
  // coming up over the next month.
  //
  // Sent as timezone-aware RFC3339 bounds (start-of-day / end-of-day in the
  // BROWSER's zone, offset included) rather than a bare YYYY-MM-DD. The server
  // parses a bare date as UTC midnight, so for an IST operator "from today"
  // used to really begin at 05:30 IST — silently dropping every reminder due
  // earlier that morning — while the upper bound over-ran by the same 5.5h.
  const DEFAULT_DUE_FROM = moment().startOf('day').format();
  const DEFAULT_DUE_TO = moment().add(30, 'days').endOf('day').format();

  // Read once at mount: the "last used" persisted filters and the default preset
  // (if any). Precedence on load, lowest → highest:
  //   built-in defaults  <  default preset  <  last-used filters
  // Last-used wins because it's the more specific signal (what the user actually
  // had on screen last time) than a standing default preset.
  const [persistedInit] = useState(readPersistedFilters);
  const [defaultPresetInit] = useState(() => getDefaultPreset(readPresets()));
  // `hydratedAssignee` records whether a stored source (last-used OR default
  // preset) supplied a team-member selection — when one did we honour it and
  // skip the "fold in self" seeding below so it can't fight the restored state.
  const hydratedAssignee = !!(
    (persistedInit && Array.isArray(persistedInit.assignee_uuids)) ||
    (defaultPresetInit && Array.isArray(defaultPresetInit.filters?.assignee_uuids))
  );

  // Team-member filter is multi-select (array of uuids, may include the
  // UNASSIGNED_ASSIGNEE sentinel). Default selection is "Unassigned + me" so
  // users land on their own queue plus anything nobody owns yet. If `me` isn't
  // resolved on the initial (cold-cache) render we start with just Unassigned
  // and the effect below folds in self once the team cache warms. An empty
  // array means no filter (all team). The due-date range always resets to the
  // default window (never persisted); everything else follows the precedence
  // documented above.
  const [filters, setFilters] = useState(() => {
    const base = {
      assignee_uuids: me?.uuid ? [UNASSIGNED_ASSIGNEE, me.uuid] : [UNASSIGNED_ASSIGNEE],
      involvement: [],
      status: 'pending',
      priority: '',
      label_uuid: '',
      // Multi-select; may mix real department uuids with the NO_DEPARTMENT
      // sentinel. Empty = Any (which includes department-less reminders).
      department_uuids: [],
      due_from: DEFAULT_DUE_FROM,
      due_to: DEFAULT_DUE_TO
    };
    if (defaultPresetInit) mergeFilterSubset(base, defaultPresetInit.filters);
    if (persistedInit) mergeFilterSubset(base, persistedInit);
    return base;
  });
  // Free-text search, sent to the server so it spans every matching reminder
  // rather than only the rows the default limit returned. Debounced so a burst
  // of keystrokes collapses into one request.
  const [titleFilter, setTitleFilter] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(titleFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [titleFilter]);

  const refresh = useCallback(() => {
    setLoading(true);
    const payload = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) payload[k] = v;
    });
    if (debouncedQ) payload.q = debouncedQ;
    list_tasks(payload)
      .then(({ tasks: rows, total: t }) => {
        setTasks(rows);
        setTotal(t);
      })
      .catch((err) => {
        // Log so failures are debuggable but don't leave the spinner stuck
        // or produce an unhandled promise rejection on the dashboard.
        // eslint-disable-next-line no-console
        console.error('reminders: list_tasks failed', err);
      })
      .finally(() => setLoading(false));
  }, [filters, debouncedQ]);

  // Latest filters, read by the realtime silent-refetch without forcing the
  // listener to re-subscribe on every filter change.
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  const qRef = useRef(debouncedQ);
  useEffect(() => {
    qRef.current = debouncedQ;
  }, [debouncedQ]);

  // Priority order for sort comparisons.
  const PRIORITY_RANK = { Urgent: 0, High: 1, Medium: 2, Low: 3, None: 4 };

  const sortedTasks = useMemo(() => {
    const base = tasks;
    if (!sortKey) return base;
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      let av;
      let bv;
      if (sortKey === 'priority') {
        av = PRIORITY_RANK[a.priority] ?? 99;
        bv = PRIORITY_RANK[b.priority] ?? 99;
      } else if (sortKey === 'due_at') {
        av = moment(a.due_at).valueOf();
        bv = moment(b.due_at).valueOf();
      }
      if (av === bv) return 0;
      return av < bv ? -factor : factor;
    });
  }, [tasks, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Jump back to page 1 whenever the visible set is re-derived (filters, the
  // search term, or sort changes). Deliberately NOT keyed on `tasks`
  // so a background refresh (e.g. toggling done) keeps the current page.
  useEffect(() => {
    setPage(1);
  }, [filters, debouncedQ, sortKey, sortDir]);

  // Clamp rather than effect-reset when the list shrinks under the current
  // page (e.g. deleting the last row of the last page).
  const pageCount = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedTasks = useMemo(
    () => sortedTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedTasks, currentPage]
  );

  // Human-readable summary of the PERSISTED filters that are currently on.
  // These are the dangerous ones: they survive a reload, so a filter set weeks
  // ago keeps hiding reminders with nothing on screen to say so. Rendered as
  // chips beside the "Filters" header with a one-click Reset.
  //
  // Deliberately NOT a "N reminders hidden" count — that needs a second,
  // unfiltered list call on every filter change just to produce a number.
  const activeFilters = useMemo(() => {
    const names = (uuids, lookup, extras = {}) =>
      (uuids || [])
        .map((u) => extras[u] || lookup(u)?.name || null)
        .filter(Boolean)
        .join(', ');
    return activeFilterSummary(filters, {
      assignee_uuids: (v) =>
        names(v, team_member_by_uuid, { [UNASSIGNED_ASSIGNEE]: 'Unassigned' }) || null,
      involvement: (v) =>
        v
          .map((k) => INVOLVEMENT_OPTIONS.find((o) => o.value === k)?.label)
          .filter(Boolean)
          .join(', ') || null,
      label_uuid: (v) => label_by_uuid(v)?.name || null,
      department_uuids: (v) =>
        names(v, department_by_uuid, { [NO_DEPARTMENT]: 'Unassigned' }) || null
    });
    // `team`, `labels` and `departments` are dependencies in practice: the
    // name lookups read the module-level caches those calls populate, so the
    // summary must recompute once they land or it renders bare counts.
  }, [filters, team, labels, departments]);

  // Renders a clickable header cell with the appropriate sort glyph.
  function SortHeader({ label, sortBy, className = '' }) {
    const active = sortKey === sortBy;
    const Icon = active ? (sortDir === 'asc' ? FaSortUp : FaSortDown) : FaSort;
    return (
      <button
        type="button"
        onClick={() => toggleSort(sortBy)}
        className={`reminders-neutral-btn inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider ${active ? 'text-content-primary' : 'text-content-secondary'} ${className}`}
        style={{ background: 'transparent' }}
      >
        {label}
        <Icon size={10} className={active ? 'text-mytPrimary' : 'text-content-muted'} />
      </button>
    );
  }

  useEffect(() => {
    list_team_members().then(setTeam);
    list_labels().then(setLabels);
    list_departments().then(setDepartments);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime updates over the myt_chat WebSocket (dedicated myt:crm packets).
  // The goserver broadcasts reminder.created / reminder.updated /
  // reminder.deleted to all connected care agents on every reminder mutation;
  // we react with a trailing-debounced silent list refetch (no full-page
  // spinner). Refetching only swaps the `tasks` rows — the create/edit form and
  // details modal keep their own state, so a background refresh can't clobber an
  // in-progress edit. Mirrors the support_inbox realtime pattern, including the
  // reconnect and tab-refocus safety nets. Registered once (refs supply the
  // latest filters).
  useEffect(() => {
    let listTimer = null;

    const refetchList = () => {
      const payload = {};
      Object.entries(filtersRef.current).forEach(([k, v]) => {
        if (v) payload[k] = v;
      });
      if (qRef.current) payload.q = qRef.current;
      list_tasks(payload)
        .then(({ tasks: rows, total: t }) => {
          setTasks(rows);
          setTotal(t);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('reminders: realtime refetch failed', err);
        });
    };
    // Trailing debounce so a burst of N packets collapses into one refetch.
    const scheduleList = () => {
      if (listTimer) clearTimeout(listTimer);
      listTimer = setTimeout(refetchList, 500);
    };

    const onReminderPacket = (pkt) => {
      switch (pkt && pkt.event) {
        case 'reminder.created':
        case 'reminder.updated':
        case 'reminder.deleted':
          scheduleList();
          break;
        default:
          break;
      }
    };
    CallbackHandler.shared.registerCrmEventListener(onReminderPacket);

    // After a WS reconnect, refetch once to catch anything missed while
    // disconnected.
    let wasConnected = ChatClient.shared.connectionState() === ConnectionState.AUTHENTICATED;
    const onConnState = (state) => {
      const nowConnected = state === ConnectionState.AUTHENTICATED;
      if (nowConnected && !wasConnected) refetchList();
      wasConnected = nowConnected;
    };
    CallbackHandler.shared.registerConnectionListener(onConnState);

    // Safety net: refetch when the tab is refocused (backgrounded tabs can miss
    // pushes / drop the socket).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchList();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // The chat WS is normally already connected by the app shell
    // (home/index.jsx → ChatClient.initialize). Deep-linking straight into
    // /reminders could beat that, so connect defensively when we're authed and
    // not yet connected. Idempotent; guarded so a missing token fails quietly.
    try {
      if (
        getAuthUserInfoOrDefault().uuid &&
        ChatClient.shared.connectionState() === ConnectionState.NOT_CONNECTED
      ) {
        Promise.resolve(ChatClient.shared.initialize()).catch(() => {});
      }
    } catch (e) {
      // no auth / already connected as another user — realtime just won't start
    }

    return () => {
      CallbackHandler.shared.unregisterCrmEventListener(onReminderPacket);
      CallbackHandler.shared.unregisterConnectionListener(onConnState);
      document.removeEventListener('visibilitychange', onVisibility);
      if (listTimer) clearTimeout(listTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link: arriving at /reminders?task=<uuid> (a shared permalink) or
  // /reminders?reminder_id=<uuid> (the CRM reminder email's "View reminder"
  // button) opens that task's details modal. The task may fall outside the
  // current filters, so resolve it with an unfiltered by-uuid lookup rather
  // than the visible list — so the deep link works even when the reminder
  // isn't on the first page. The param is then stripped so closing the modal
  // / refreshing doesn't reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uuid = params.get('reminder_id') || params.get('task');
    if (!uuid) return;
    list_tasks({ uuids: [uuid] })
      .then(({ tasks: rows }) => {
        const found = (rows || []).find((t) => t.uuid === uuid);
        if (found) setViewingTask(found);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('reminders: deep-link resolve failed', err);
      })
      .finally(() => {
        params.delete('reminder_id');
        params.delete('task');
        history.replace({ pathname: location.pathname, search: params.toString() });
      });
    // Resolve once for the incoming link (mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (patch) => setFilters((p) => ({ ...p, ...patch }));

  // Apply a saved preset's stored filter values over the current filters. Only
  // the persisted keys are touched (via mergeFilterSubset) — the due-date range
  // and title search are left as-is. The persistence effect then records the
  // result as the new "last used" state.
  const applyPresetFilters = (presetFilters) =>
    setFilters((p) => {
      const next = { ...p };
      mergeFilterSubset(next, presetFilters);
      return next;
    });

  // Fold "self" into the default team-member selection once `me` resolves from
  // the async team cache (cold-load case, where the initial state above could
  // only seed Unassigned). Runs at most once, and only while the filter is
  // still the untouched Unassigned-only default — so it never clobbers a
  // selection the user has already changed (including deliberately clearing it).
  const seededSelfRef = useRef(false);
  useEffect(() => {
    if (seededSelfRef.current) return;
    // A restored selection from localStorage is authoritative — never seed over
    // it.
    if (hydratedAssignee) {
      seededSelfRef.current = true;
      return;
    }
    if (!me?.uuid) return;
    seededSelfRef.current = true;
    setFilters((p) => {
      const cur = p.assignee_uuids || [];
      const untouchedDefault = cur.length === 1 && cur[0] === UNASSIGNED_ASSIGNEE;
      if (!untouchedDefault || cur.includes(me.uuid)) return p;
      return { ...p, assignee_uuids: [UNASSIGNED_ASSIGNEE, me.uuid] };
    });
  }, [me, hydratedAssignee]);

  // Persist the filter subset on every change (and on mount). The due-date range
  // and title search are intentionally omitted (see PERSISTED_FILTER_KEYS).
  // clearFilters resets `filters`, which this effect then overwrites in storage.
  useEffect(() => {
    try {
      const toPersist = {};
      PERSISTED_FILTER_KEYS.forEach((k) => {
        toPersist[k] = filters[k];
      });
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toPersist));
    } catch {
      /* ignore quota / serialization errors — persistence is best-effort */
    }
  }, [filters]);

  // Reset every filter to "off". The persistence effect above runs on the next
  // render and overwrites the localStorage payload with these cleared values,
  // so Reset genuinely clears the sticky filters rather than just the in-memory
  // ones — the whole point, given a forgotten persisted filter is what hid
  // reminders across sessions in the first place.
  const clearFilters = () =>
    setFilters({
      assignee_uuids: [],
      involvement: [],
      status: '',
      priority: '',
      label_uuid: '',
      department_uuids: [],
      due_from: DEFAULT_DUE_FROM,
      due_to: DEFAULT_DUE_TO
    });

  const handleSubmit = (payload) => {
    if (payload.uuid) {
      const { uuid, ...patch } = payload;
      return update_task(uuid, patch, me?.name).then(() => {
        setShowCreate(false);
        setEditingTask(null);
        refresh();
      });
    }
    return create_task(payload, me?.name).then(() => {
      setShowCreate(false);
      refresh();
    });
  };

  const handleToggleComplete = (task) =>
    update_task(
      task.uuid,
      { status: task.status === 'completed' ? 'pending' : 'completed' },
      me?.name
    ).then(() => refresh());

  const handleStartEdit = (task) => {
    setEditingTask(task);
    setShowCreate(true);
  };
  const handleCloseModal = () => {
    setShowCreate(false);
    setEditingTask(null);
  };

  // Stage a delete request; the actual delete fires when the user confirms in
  // the in-app ConfirmationPopup below (avoids native window.confirm per the
  // codebase guideline in .github/copilot-instructions.md).
  const handleDelete = (task) => setTaskPendingDelete(task);

  const confirmDelete = () => {
    const task = taskPendingDelete;
    if (!task) return;
    delete_task(task.uuid).then(() => {
      if (editingTask?.uuid === task.uuid) setEditingTask(null);
      if (viewingTask?.uuid === task.uuid) setViewingTask(null);
      setTaskPendingDelete(null);
      refresh();
    });
  };

  // Render bridge: react-tooltip passes the row's data-tooltip-content
  // string (the task uuid) so we resolve it here against the in-memory map
  // and hand the task + operator-pref timezone to the shared tooltip body.
  const renderTaskTooltip = (taskUuid) => {
    const task = tasksByUuid[taskUuid];
    if (!task) return null;
    return <TaskTooltipContent task={task} tz={ianaTimezone} />;
  };

  return (
    <CommonHeader title="Reminders">
      <div className="reminders-scope">
        {/* Dedicated tooltip for task descriptions — pre-wrap preserves
            newlines from multi-line descriptions; the dark "card" look
            with soft shadow + rounded corners matches modern productivity
            tools (Linear / Notion). */}
        <ReactTooltip
          id="reminders-task-tooltip"
          place="top"
          delayShow={150}
          delayHide={60}
          render={({ content }) => renderTaskTooltip(content)}
          style={taskTooltipStyle}
          opacity={1}
        />
        {/*
          Active persisted filters. Rendered above the controls so the state
          that survives a reload is the first thing read on the page — a
          filter left on weeks ago used to hide reminders with no indication
          anywhere on screen. Each chip is a one-click "turn this one off".
        */}
        {activeFilters.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-state-warning bg-state-warning-bg px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-state-warning-text">
              Active filters
            </span>
            {activeFilters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => updateFilter({ [f.key]: Array.isArray(filters[f.key]) ? [] : '' })}
                title={`Clear "${f.label}"`}
                aria-label={`Clear filter ${f.label}`}
                className="reminders-secondary-btn inline-flex max-w-[280px] items-center gap-1.5 text-[11px]"
              >
                <span className="truncate">{f.label}</span>
                <FaTimes size={9} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                clearFilters();
                setTitleFilter('');
              }}
              className="reminders-secondary-btn ml-auto inline-flex items-center gap-1.5 text-[11px]"
            >
              <FaRedoAlt size={9} /> Clear all
            </button>
          </div>
        ) : null}
        {/* Top bar */}
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-line bg-surface-card p-3 shadow-sm">
          <div className="flex items-center gap-1.5 self-center pr-2 text-content-secondary">
            <FaFilter size={11} />
            <span className="text-xs font-semibold uppercase tracking-wider">Filters</span>
            {activeFilters.length > 0 ? (
              <span
                title={activeFilters.map((f) => f.label).join(' · ')}
                className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-state-warning-bg px-1 text-[10px] font-bold text-state-warning-text"
              >
                {activeFilters.length}
              </span>
            ) : null}
          </div>
          <div className="min-w-[160px]">
            <FilterLabel>Presets</FilterLabel>
            <RemindersPresets currentFilters={filters} onApply={applyPresetFilters} />
          </div>
          <div>
            <FilterLabel>Due date</FilterLabel>
            <DateRangeFilter
              from={filters.due_from}
              to={filters.due_to}
              onChange={({ from, to }) => updateFilter({ due_from: from, due_to: to })}
            />
          </div>
          <div className="min-w-[260px]">
            <FilterLabel>Team member</FilterLabel>
            <SearchableSelect
              multi
              value={filters.assignee_uuids}
              options={[
                { value: UNASSIGNED_ASSIGNEE, label: 'Unassigned', sublabel: 'No assignee' },
                ...team.map((m) => ({
                  value: m.uuid,
                  label: `${m.name}${me && m.uuid === me.uuid ? ' (me)' : ''}`,
                  sublabel: m.email
                }))
              ]}
              onChange={(v) => updateFilter({ assignee_uuids: v })}
              /* Empty selection = no filter = every team member. */
              placeholder="All team"
              renderIcon={() => <FaUserCircle className="text-content-muted" size={12} />}
              /* Wider popover so name + email read comfortably. */
              popoverClassName="w-[min(420px,calc(100vw-40px))]"
            />
          </div>
          <div className="min-w-[200px]">
            <FilterLabel>My reminders</FilterLabel>
            <SearchableSelect
              multi
              value={filters.involvement}
              options={INVOLVEMENT_OPTIONS}
              onChange={(v) => updateFilter({ involvement: v })}
              /* Empty selection = filter inactive. The requesting user is
                 resolved server-side from the auth token — no uuid is sent. */
              placeholder="Anyone"
              renderIcon={() => <FaUserCircle className="text-content-muted" size={12} />}
            />
          </div>
          <div className="min-w-[140px]">
            <FilterLabel>Status</FilterLabel>
            <SearchableSelect
              value={filters.status}
              options={[{ value: '', label: 'Any' }, ...STATUS_OPTIONS]}
              onChange={(v) => updateFilter({ status: v })}
              placeholder="Any"
            />
          </div>
          <div className="min-w-[150px]">
            <FilterLabel>Priority</FilterLabel>
            <SearchableSelect
              value={filters.priority}
              options={[
                { value: '', label: 'Any' },
                ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))
              ]}
              onChange={(v) => updateFilter({ priority: v })}
              placeholder="Any"
              renderIcon={(opt) =>
                opt.value ? <PriorityIcon priority={opt.value} size={14} /> : null
              }
              renderRow={(opt, selected) => (
                <>
                  {opt.value ? (
                    <PriorityIcon priority={opt.value} size={14} />
                  ) : (
                    <span style={{ width: 14 }} />
                  )}
                  <span className="flex-1 truncate">{opt.label}</span>
                  {selected ? <span className="text-state-success text-xs">✓</span> : null}
                </>
              )}
            />
          </div>
          <div className="min-w-[150px]">
            <FilterLabel>Label</FilterLabel>
            <SearchableSelect
              value={filters.label_uuid}
              options={[
                { value: '', label: 'Any' },
                ...labels.map((l) => ({ value: l.uuid, label: l.name, color: l.color }))
              ]}
              onChange={(v) => updateFilter({ label_uuid: v })}
              placeholder="Any"
              renderIcon={(opt) =>
                opt.color ? (
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: opt.color }}
                  />
                ) : null
              }
            />
          </div>
          {/*
            Depart — multi-select, same shape as Team member. The "Unassigned"
            option is the sentinel for department_uuid IS NULL: without it,
            picking any department silently excluded every department-less
            reminder, because SQL equality never matches NULL. It combines with
            real departments (the backend ORs them), and an empty selection
            means Any — which still includes the department-less rows.
          */}
          <div className="min-w-[200px]">
            <FilterLabel>Depart</FilterLabel>
            <SearchableSelect
              multi
              value={filters.department_uuids}
              options={[
                { value: NO_DEPARTMENT, label: 'Unassigned', sublabel: 'No department' },
                ...departments.map((d) => ({ value: d.uuid, label: d.name, color: d.color }))
              ]}
              onChange={(v) => updateFilter({ department_uuids: v })}
              /* Empty selection = no filter = every department. */
              placeholder="Any"
              renderIcon={(opt) =>
                opt.color ? (
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: opt.color }}
                  />
                ) : null
              }
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                clearFilters();
                setTitleFilter('');
              }}
              title="Clear filters"
              className="reminders-secondary-btn inline-flex items-center gap-1.5 text-xs"
            >
              <FaRedoAlt size={10} /> Reset
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="reminders-primary-btn inline-flex items-center gap-1.5 text-sm font-medium"
            >
              <FaPlus size={11} /> New reminder
            </button>
          </div>
        </div>

        {/* Free-text search — debounced and sent to the server, so it matches
            title and description across every reminder, not just this page. */}
        <div className="mt-3 flex items-center gap-2">
          <div className="relative w-full max-w-sm">
            <input
              type="text"
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              placeholder="Search all reminders…"
              className="h-[34px] w-full rounded-md border border-line bg-surface-card px-3 py-0 pr-8 text-sm text-content-primary focus:border-mytPrimary focus:outline-none focus:ring-1 focus:ring-mytPrimary/40"
            />
            {titleFilter ? (
              <button
                type="button"
                onClick={() => setTitleFilter('')}
                aria-label="Clear search"
                className="reminders-icon-btn absolute right-1 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center"
              >
                <FaTimes size={10} />
              </button>
            ) : null}
          </div>
          {debouncedQ ? (
            <span className="text-xs text-content-muted">
              {total > tasks.length
                ? `${total.toLocaleString()} match — showing the first ${tasks.length}`
                : `${total.toLocaleString()} match${total === 1 ? '' : 'es'}`}
            </span>
          ) : null}
        </div>

        {/* Table */}
        <div className="mt-2 flex min-h-[420px] flex-col overflow-hidden rounded-lg border border-line bg-surface-card shadow-sm">
          <div className="grid grid-cols-12 gap-2 border-b border-line bg-surface-sunken px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-content-secondary">
            <div className="col-span-1 text-center">Done</div>
            <div className="col-span-1 flex justify-center">
              <SortHeader label="Priority" sortBy="priority" />
            </div>
            <div className="col-span-4">Task</div>
            <div className="col-span-1">
              <SortHeader label="Due" sortBy="due_at" />
            </div>
            <div className="col-span-1">Labels</div>
            <div className="col-span-1">Depart</div>
            <div className="col-span-1">Assignees</div>
            <div className="col-span-1">User</div>
            <div className="col-span-1">Actions</div>
          </div>

          {(loading && tasks.length === 0) || (!loading && sortedTasks.length === 0) ? (
            // Initial load / empty state: the rest of the page (filters, header,
            // new-reminder button) stays interactive — only the list area shows
            // a placeholder.
            <div className="flex flex-1 flex-col items-center justify-center px-3 py-10 text-center text-sm text-content-muted">
              <FaRegBell
                className={`mb-2 text-content-muted ${loading ? 'animate-pulse' : ''}`}
                size={22}
              />
              {loading ? 'Loading reminders…' : 'No reminders match these filters.'}
            </div>
          ) : (
            pagedTasks.map((task) => {
              const taskAssignees = (task.assignee_uuids || [])
                .map(team_member_by_uuid)
                .filter(Boolean);
              const linked = linked_user_by_uuid(task.linked_user_uuid);
              const taskLabels = (task.label_uuids || []).map(label_by_uuid).filter(Boolean);
              const taskDepartment = department_by_uuid(task.department_uuid);
              const overdue = isPastDue(task);
              const dueWithinHour = isDueWithinHour(task);
              const dueSoon = isDueSoon(task);
              // Resolve the Due column colour in one place:
              //   completed → light gray (faded — matches the strikethrough title)
              //   past pending → red
              //   ≤1 hour pending → green (drop everything and act)
              //   ≤24 hours pending → blue (it's coming up)
              //   else → default gray
              const isPending = task.status !== 'completed';
              let dueColorCls = 'text-content-secondary';
              if (!isPending) dueColorCls = 'text-content-muted';
              else if (overdue) dueColorCls = 'text-state-error font-medium';
              else if (dueWithinHour) dueColorCls = 'text-state-success font-medium';
              else if (dueSoon) dueColorCls = 'text-state-info font-medium';
              return (
                <div
                  key={task.uuid}
                  className={`grid grid-cols-12 items-start gap-2 border-b border-line px-3 py-2.5 text-sm transition hover:bg-surface-sunken ${
                    overdue ? 'bg-state-error-bg-subtle' : ''
                  }`}
                >
                  <div className="col-span-1 flex justify-center">
                    <button
                      type="button"
                      onClick={() => handleToggleComplete(task)}
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content={
                        task.status === 'completed' ? 'Mark as pending' : 'Mark as done'
                      }
                      aria-label={task.status === 'completed' ? 'Mark as pending' : 'Mark as done'}
                      className="reminders-icon-btn"
                    >
                      {task.status === 'completed' ? (
                        <FaCheckSquare className="text-state-success" size={18} />
                      ) : (
                        <FaRegSquare size={18} />
                      )}
                    </button>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <PriorityIcon priority={task.priority} size={24} />
                  </div>
                  {/*
                    Clicking the title opens the notes/details modal — same
                    target as the FaRegNoteSticky icon in the row's action
                    column, so operators can drill into a task straight from
                    its headline. Rendered as a button (styled to look like
                    the prior div) for proper keyboard + a11y support.
                  */}
                  <button
                    type="button"
                    onClick={() => setViewingTask(task)}
                    data-tooltip-id="reminders-task-tooltip"
                    data-tooltip-content={task.uuid}
                    className={`col-span-4 flex min-w-0 cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-left ${
                      task.status === 'completed'
                        ? 'text-content-muted line-through'
                        : 'text-content-primary'
                    }`}
                  >
                    <FaCircleInfo size={12} className="shrink-0 text-content-muted" />
                    <span className="truncate">{task.title}</span>
                  </button>
                  <div
                    className={`col-span-1 inline-flex items-center gap-1 text-xs ${dueColorCls}`}
                  >
                    <FaClock size={10} />
                    <span className="truncate">{formatRelativeDue(task)}</span>
                  </div>
                  <div className="col-span-1 flex flex-col items-start gap-1">
                    {taskLabels.length === 0 ? (
                      <span className="text-[11px] text-content-muted">—</span>
                    ) : (
                      taskLabels.map((l) => (
                        <span
                          key={l.uuid}
                          className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                          style={{ background: l.color, color: readableTextOn(l.color) }}
                        >
                          <FaTag size={7} className="opacity-50" />
                          {l.name}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="col-span-1 flex flex-col items-start gap-1">
                    {taskDepartment ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                        style={{
                          background: taskDepartment.color,
                          color: readableTextOn(taskDepartment.color)
                        }}
                      >
                        <FaBuilding size={7} className="opacity-50" />
                        {taskDepartment.name}
                      </span>
                    ) : (
                      <span className="text-[11px] text-content-muted">—</span>
                    )}
                  </div>
                  <div className="col-span-1 flex flex-col items-start gap-0.5 text-xs text-content-secondary">
                    {taskAssignees.length === 0 ? (
                      <span className="text-content-muted">—</span>
                    ) : (
                      taskAssignees.map((a) => {
                        // Compact display: "Rohit Sharma" → "Rohit S".
                        // Keeps full name in the title for hover.
                        const parts = (a.name || '').trim().split(/\s+/);
                        const short =
                          parts.length > 1
                            ? `${parts[0]} ${parts[parts.length - 1][0]}`
                            : parts[0] || '';
                        return (
                          <span
                            key={a.uuid}
                            className="inline-flex items-center gap-1 truncate"
                            title={`${a.name}${a.email ? ` · ${a.email}` : ''}`}
                          >
                            <FaUserCircle size={11} className="text-content-muted" />
                            <span className="truncate">{short}</span>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <div className="col-span-1 flex items-center gap-1 text-xs text-content-secondary">
                    {linked ? (
                      (() => {
                        const isTeacher = linked.role === 'teacher';
                        // The CMS uses query-string-based profile routes:
                        //   /studentprofile?id=<uuid> · /teacherprofile?id=<uuid>
                        const href = `${isTeacher ? '/teacherprofile' : '/studentprofile'}?id=${encodeURIComponent(linked.uuid)}`;
                        const firstName = (linked.name || '').split(' ')[0];
                        const roleLabel = isTeacher ? 'Coach' : 'Student';
                        const RoleIcon = isTeacher ? TbCircleLetterT : TbCircleLetterS;
                        // Purple for teachers, MYT theme orange for students —
                        // applied to both the icon and the first-name text.
                        const roleColor = isTeacher ? '#9333ea' : '#ee731b';
                        return (
                          <Link
                            to={href}
                            className="inline-flex items-center gap-1 truncate hover:underline"
                            style={{ color: roleColor }}
                            title={`Open ${roleLabel.toLowerCase()} profile — ${linked.name}`}
                          >
                            <RoleIcon size={18} />
                            <span className="truncate">{firstName}</span>
                          </Link>
                        );
                      })()
                    ) : (
                      <span className="text-content-muted">—</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setViewingTask(task)}
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content="View details & notes"
                      aria-label="View reminder details"
                      className="reminders-icon-btn"
                    >
                      <FaRegNoteSticky size={15} />
                    </button>
                    <RowActionsMenu
                      items={[
                        {
                          label: 'Edit',
                          icon: FaRegEdit,
                          onClick: () => handleStartEdit(task)
                        },
                        {
                          label: 'Delete',
                          icon: FaRegTrashAlt,
                          destructive: true,
                          onClick: () => handleDelete(task)
                        }
                      ]}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] text-content-muted">
            {sortedTasks.length === 0
              ? 'Showing 0 reminders'
              : `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                  currentPage * PAGE_SIZE,
                  sortedTasks.length
                )} of ${sortedTasks.length} reminder${sortedTasks.length === 1 ? '' : 's'}`}
            {total > sortedTasks.length
              ? ` (${total.toLocaleString()} total — narrow your filters)`
              : ''}
            {loading && tasks.length > 0 ? ' · Refreshing…' : ''}
          </div>
          {pageCount > 1 ? (
            <Paginator curr_page={currentPage} page_limit={pageCount} pageChange={setPage} />
          ) : null}
        </div>

        {/* Create / edit / clone modal. When editing an existing reminder the
            modal widens and shows its notes thread beside the form (notes only
            attach to a saved reminder, so creating shows the form alone). */}
        <CustomModal
          show={showCreate}
          close={handleCloseModal}
          title={editingTask ? 'Edit reminder' : 'New reminder'}
          inner_style={{
            width: editingTask ? 'min(1040px, 96vw)' : 'min(640px, 96vw)',
            maxWidth: 'none'
          }}
        >
          {editingTask ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="min-w-0">
                <ReminderForm
                  key={editingTask.uuid}
                  initial={taskToFormInitial(editingTask)}
                  defaultAssigneeUuid={me?.uuid}
                  selfUuid={me?.uuid}
                  onSubmit={handleSubmit}
                  onCancel={handleCloseModal}
                  submitLabel="Save"
                />
              </div>
              <div className="flex min-h-0 min-w-0 flex-col md:border-l md:border-line md:pl-6">
                <ReminderNotes
                  taskUuid={editingTask.uuid}
                  notes={editingTask.notes}
                  onUpdated={(updated) => {
                    setEditingTask(updated);
                    refresh();
                  }}
                />
              </div>
            </div>
          ) : (
            <ReminderForm
              // Remount when the seed changes so internal form state resets.
              key="new"
              initial={taskToFormInitial(editingTask)}
              defaultAssigneeUuid={me?.uuid}
              selfUuid={me?.uuid}
              onSubmit={handleSubmit}
              onCancel={handleCloseModal}
              submitLabel="Create"
            />
          )}
        </CustomModal>

        {/* View-details modal (read-only metadata + notes thread) */}
        <ReminderDetailsModal
          show={!!viewingTask}
          close={() => setViewingTask(null)}
          task={viewingTask}
          onUpdated={(updated) => {
            // Reflect new note locally and in the list view.
            setViewingTask(updated);
            refresh();
          }}
        />

        {/* In-app delete confirmation, replaces the prior window.confirm call. */}
        <CustomModal show={!!taskPendingDelete} close={() => setTaskPendingDelete(null)}>
          <ConfirmationPopup
            title={
              taskPendingDelete ? `Delete "${taskPendingDelete.title}"? This can't be undone.` : ''
            }
            close_modal={() => setTaskPendingDelete(null)}
            confirm={confirmDelete}
          />
        </CustomModal>
      </div>
    </CommonHeader>
  );
}

export default Reminders;
